import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/i18n";
import { getFixedPrice, formatFixedPrice } from "@/lib/fixedPrices";
import { Sparkles, ArrowRight, Check, HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import bundleHero from "@/assets/products/bundle-hero.png";

const BUNDLE_HANDLE = "complete-pdrn-trio-bundle";

// Individual product handles for "separately" price calculation
const INDIVIDUAL_HANDLES = [
  'pink-glow-serum-with-pdrn-peptides',
  'smart-capsule-cream-with-collagen',
  'pdrn-pink-sheet-masks-10-pack',
];

interface IncludedItem {
  label: string;
  tooltipKey: string;
}

export function BundleUpsell() {
  const { t, currency } = useLanguage();
  const displayCurrency = currency || 'USD';

  const fp = getFixedPrice(BUNDLE_HANDLE, displayCurrency);
  if (!fp) return null;

  const saleFormatted = formatFixedPrice(fp.sale, displayCurrency);
  const originalFormatted = formatFixedPrice(fp.original, displayCurrency);
  const savings = fp.original - fp.sale;
  const savingsFormatted = formatFixedPrice(savings, displayCurrency);

  // Calculate total if bought separately
  const separatelyTotal = INDIVIDUAL_HANDLES.reduce((sum, handle) => {
    const price = getFixedPrice(handle, displayCurrency);
    return sum + (price ? price.sale : 0);
  }, 0);
  const separatelyFormatted = formatFixedPrice(separatelyTotal, displayCurrency);

  const includedItems: IncludedItem[] = [
    { label: t('upsell.includesSerum'), tooltipKey: 'upsell.serumTooltip' },
    { label: t('upsell.includesCream'), tooltipKey: 'upsell.creamTooltip' },
    { label: t('upsell.includesMasks'), tooltipKey: 'upsell.masksTooltip' },
  ];

  return (
    <section className="my-8 md:my-10 max-w-4xl mx-auto" aria-label={t('upsell.badge')}>
      <div className="overflow-hidden rounded-2xl bg-gradient-pink border-2 border-primary/20 shadow-pink">
        <div className="grid md:grid-cols-[280px_1fr] lg:grid-cols-[320px_1fr]">
          {/* Bundle Image */}
          <div className="relative bg-white/40 p-6 flex items-center justify-center">
            <img
              src={bundleHero}
              alt={t('upsell.title')}
              className="w-full max-w-[240px] md:max-w-none object-contain"
              loading="lazy"
            />
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 flex flex-col justify-center">
            {/* Badge */}
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                {t('upsell.badge')}
              </span>
            </div>

            <h3 className="text-xl md:text-2xl font-bold text-foreground mb-2">
              {t('upsell.whyBundle')}
            </h3>

            <p className="text-sm text-muted-foreground mb-4">
              {t('upsell.whyBundleDesc')}
            </p>

            {/* Included items with tooltip popovers */}
            <ul className="space-y-2 mb-5">
              {includedItems.map((item, idx) => (
                <li key={idx} className="flex items-center gap-2.5">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-primary" />
                  </span>
                  <span className="text-sm font-medium text-foreground">{item.label}</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="flex-shrink-0 text-muted-foreground hover:text-primary transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 rounded-full"
                        aria-label={item.label}
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent
                      side="top"
                      align="center"
                      className="w-60 p-3 text-xs leading-relaxed text-foreground bg-background border border-border shadow-md"
                    >
                      {t(item.tooltipKey)}
                    </PopoverContent>
                  </Popover>
                </li>
              ))}
            </ul>

            {/* Pricing */}
            <div className="flex flex-wrap items-baseline gap-3 mb-2">
              <span className="text-2xl md:text-3xl font-extrabold text-foreground">{saleFormatted}</span>
              <span className="text-sm text-muted-foreground line-through opacity-60">{originalFormatted}</span>
              {savings > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-success/10 text-success text-xs font-semibold rounded-full">
                  <Sparkles className="w-3 h-3" />
                  {t('upsell.saveBadge', { amount: savingsFormatted })}
                </span>
              )}
            </div>

            <p className="text-xs text-muted-foreground mb-5">
              {t('upsell.separatelyPrice', { amount: separatelyFormatted })}
            </p>

            {/* CTA */}
            <Button
              asChild
              size="lg"
              className="w-full bg-gradient-button shadow-pink hover:opacity-90 transition-opacity group text-sm md:text-base"
            >
              <Link to={`/product/${BUNDLE_HANDLE}`}>
                {t('upsell.cta')}
                <ArrowRight className="w-4 h-4 ms-2 group-hover:translate-x-1 rtl:group-hover:-translate-x-1 transition-transform" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
